import React from 'react';
import moment from 'moment-timezone';
import { Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Download, Visibility } from '@mui/icons-material';
import ActionButton, { ActionSlot } from '../../FunctionalApps/inspect/processed-fluids-route-session/DocumentActionButton';
import { useGetDeliveryDocumentVersions } from '../../../../data/generic-queries/delivery-documents';
import { deliveryDocumentLabel, reportTypeLabel } from './constants';

const MISSING = 'File missing from storage';
const text = (value) => (value == null || value === '' ? '-' : value);

const DeliveryReportHistoryDialog = ({ row, onClose, onView, onDownload }) => {
    const { data: versions = [], isLoading, isError } = useGetDeliveryDocumentVersions(row?._id);

    return (
        <Dialog open={!!row} onClose={onClose} aria-labelledby="delivery-report-history-title" maxWidth="md" fullWidth>
            <DialogTitle id="delivery-report-history-title">
                Delivery Report history
                {row && (
                    <Typography variant="body2" color="text.secondary">
                        {deliveryDocumentLabel(row)}
                        {row.route_id?.name ? ` · ${row.route_id.name}` : ''} · {moment(row.created_at).format('MM/DD/YYYY')}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                {isLoading && <LinearProgress />}
                {isError && <Alert severity="error">Unable to load the report history. Close this and try again.</Alert>}
                {!isLoading && !isError && versions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No versions have been recorded for this route session.
                    </Typography>
                )}
                {versions.length > 0 && (
                    <Table size="small" aria-label="Report versions">
                        <TableHead>
                            <TableRow>
                                <TableCell>Version</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Generated</TableCell>
                                <TableCell>Generated By</TableCell>
                                <TableCell />
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {versions.map((version) => (
                                <TableRow key={version._id} hover>
                                    <TableCell>v{version.version}</TableCell>
                                    <TableCell>{reportTypeLabel(version)}</TableCell>
                                    <TableCell>{version.generated_at ? moment(version.generated_at).format('MM/DD/YYYY HH:mm') : '-'}</TableCell>
                                    <TableCell>{text(version.generated_by?.name)}</TableCell>
                                    <TableCell>
                                        {version.current && <Chip label="Current" size="small" color="primary" />}
                                        {!version.available && <Chip label={MISSING} size="small" variant="outlined" sx={{ ml: version.current ? 1 : 0 }} />}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            <ActionSlot>
                                                <ActionButton label={`View v${version.version}`} tooltip={version.available ? 'View' : MISSING} Icon={Visibility} disabled={!version.available} onClick={() => onView(version)} />
                                            </ActionSlot>
                                            <ActionSlot>
                                                <ActionButton label={`Download v${version.version}`} tooltip={version.available ? 'Download' : MISSING} Icon={Download} disabled={!version.available} onClick={() => onDownload(version)} />
                                            </ActionSlot>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeliveryReportHistoryDialog;
